import { pipe, NonEmptyArray, Option } from "fp";
import React from "react";
import styled from "styled-components";

import { Asset } from "~/Asset";
import * as Driver from "~/Driver";
import * as ELD from "~/ELD";
import * as Layout from "~/Layout";
import { NotApplicable } from "~/NotApplicable";
import * as Table from "~/Table";
import { Time } from "~/Time";
import * as Typography from "~/Typography";

import popper from "./Popper.svg";

export const Component = () =>
  pipe(
    ELD.useAllVisible(),
    NonEmptyArray.fromReadonlyArray,
    Option.fold(
      () => [<Empty.Component key="empty" />],
      NonEmptyArray.map((eld) => <Row.Component key={eld.id} {...eld} />)
    ),
    (rows) => <>{rows}</>
  );

namespace Empty {
  export const Component = () => {
    const search = ELD.Search.useSearch();
    const { interval } = ELD.useInterval();

    // An empty table means two different things, and saying the wrong one sends
    // the reader to the date range when the search box is what excluded
    // everything.
    return (
      <Table.Wide.Component colSpan={ELD.Table.colSpan()}>
        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
        <Table.Wide.Image.Component src={popper} />
        <Table.Wide.Caption.Component>
          {search ? (
            <>No submissions matching “{search}”.</>
          ) : (
            <>
              No submissions since&nbsp;
              {Time.DateTime.toDateOnlyMMMDYYYY(interval.start)}.
            </>
          )}
        </Table.Wide.Caption.Component>
      </Table.Wide.Component>
    );
  };
}

namespace Row {
  export const Component: Component<ELD.ELD> = (eld) => (
    <Table.Row.Component
      key={`${eld.id}${eld.interval.start.dateTime}`}
      {...eld}
    >
      <Column.Component>
        {Time.DateTime.toFormat(eld.submission)("MMM D, YYYY hh:mm A")}
      </Column.Component>
      <Column.Component>
        <ELD.Submission.Submitter.Component
          submitter={eld.submission.submitter}
        />
      </Column.Component>
      <Column.Component>
        <Driver.Name.Component {...eld.driver} />
      </Column.Component>
      <Column.Component>
        <Time.Interval
          {...eld}
          Start={(dateTime) => (
            <>{Time.DateTime.toDateOnlyMMMDYYYY(dateTime)}</>
          )}
          Stop={(dateTime) => <>{Time.DateTime.toDateOnlyMMMDYYYY(dateTime)}</>}
          Separator={() => <> – </>}
          Duration={() => null}
        />
      </Column.Component>
      <Column.Component>{eld.sentTo}</Column.Component>
      <Column.Component>
        {pipe(
          NonEmptyArray.fromReadonlyArray(eld.assets),
          Option.fold(
            () => [<NotApplicable key="none" />],
            NonEmptyArray.map(({ asset }) => (
              <Asset.Link key={asset.id} asset={asset}>
                {Asset.name({ asset })}
              </Asset.Link>
            ))
          )
        )}
      </Column.Component>
      <Column.Component>
        <ELD.Submission.Status.Component
          id={eld.id}
          status={eld.submission.status}
        />
      </Column.Component>
      <Column.Component>
        {pipe(
          ELD.Submission.Status.failureFromStatus(eld.submission.status),
          (failure) =>
            !failure ? (
              <NotApplicable />
            ) : (
              <ELD.Submission.Status.Failure.Resend.Component
                id={{ id: eld.id }}
                failure={failure}
              />
            )
        )}
      </Column.Component>
      <Column.Component>{eld.reference}</Column.Component>
    </Table.Row.Component>
  );
}

export namespace Column {
  type Props = { readonly width?: string; readonly center?: boolean };

  export const Component: React.Fn<Props> = ({ children, ...props }) => (
    <Style {...props}>{children}</Style>
  );

  const Style = styled(Table.Column.Component)<Props>`
    width: ${({ width }) => width ?? "unset"};
    max-width: ${({ width }) => width ?? "unset"};
    text-align: ${({ center }) => (center ? "center" : "left")};
  `;
}

export namespace Detail {
  export const Component: React.Fn = ({ children }) => (
    <Style>
      <Typography.Component small>
        <Layout.Inline.Component top>{children}</Layout.Inline.Component>
      </Typography.Component>
    </Style>
  );

  const Style = styled.div`
    display: table-cell;
  `;
}
